/**
 * What a ROUTE ERROR BOUNDARY reports — the one seam both of the app's route
 * boundaries use, so what they report cannot drift from each other or from what
 * they RENDER.
 *
 * There are two of them: `FeatureErrorBoundary` (re-exported as `ErrorBoundary`
 * by every layout route) and `root-layout`'s own, which webroutesgen attaches
 * STATICALLY to the `locale` wrapper because a lazy boundary cannot be resolved
 * for a URL that matches no route. They had identical jobs and three identical
 * defects, and only one copy was ever fixed:
 *
 *   * **Reporting during RENDER.** A boundary re-renders; a log is a side
 *     effect. One error produced as many reports as renders, inflating
 *     `web_browser_errors{component}` and burning the beacon budget. It belongs
 *     in an effect, keyed on the error so a genuinely new one still reports.
 *     ⚠ Honestly: an effect is not a once-per-error guarantee — StrictMode
 *     mounts, unmounts and remounts in dev, so this still fires twice there.
 *     What it removes is the UNBOUNDED count in production, which is the half
 *     that reached the gauge.
 *   * **`new Error(String(error))`.** An `ErrorResponse` is not an `Error`, so
 *     that produced the literal message `Error: [object Object]` — status,
 *     statusText and the loader's own `data` all discarded, which is the
 *     identity-losing shape this whole change is about, one layer down.
 *   * **A routed verdict reported as an application fault.** Since the `/_data/`
 *     transport began relaying a loader's refusal, a client-side navigation to a
 *     deleted or unpublished entity arrives at a boundary as an `ErrorResponse`
 *     and renders as the not-found page — an ordinary outcome of following a
 *     stale link.
 *
 * It lives in `shared/hooks/` and not beside the frame codec in `shared/lib/`
 * because `shared/lib` is framework-agnostic by rule (#16, Domain Purity): a
 * module there may not import React at all.
 *
 * The hook returns the routed verdict it reported, so the heading the boundary
 * renders and the level it logged at read the SAME value by construction. They
 * did not: `is404` was computed for the heading and never consulted by the
 * logger.
 */
import { useEffect } from 'react';
import { isRouteErrorResponse, useRouteError, type ErrorResponse } from 'react-router';

import { clientLogger, type WebLogComponent } from '../lib/client-logger';

/**
 * The status at which a routed verdict stops being an outcome the reader
 * NAVIGATED into and becomes a fault they OBSERVED.
 *
 * ⛔ The level is NOT a way to keep an expected 404 out of the alertable
 * counter, and the comment that used to say so was simply false: the telemetry
 * ingest (`server/api/telemetry.ts`) promotes `error` AND `warn` into
 * `web_browser_errors_total`, `web/manifests/metrics.yaml` declares both as
 * legal values of that metric's `level` label, and the `WebBrowserErrorSpike`
 * alert (`manifests/observability.bzl`) sums the counter with no `level`
 * selector at all. A demotion moves a LABEL and changes no count and no alert.
 *
 * What the level does buy is the LOKI read, and that is why the split is by
 * status rather than by "is it routed": a 5xx `ErrorResponse` is a BACKEND fault
 * the browser observed, and `{app="web"} |= "level\\":50"` — the query an
 * operator reaches for during an incident — must keep finding it. Demoting the
 * whole routed family to `warn` took client-observed server faults out of that
 * query, which is a strictly worse trade than the one it was made for.
 */
const OBSERVED_FAULT_STATUS = 500;

/**
 * Read the router's error, report it exactly as its kind and status deserve, and
 * hand back the routed verdict (or `undefined` for a genuine crash) for the
 * boundary to render from.
 */
export function useReportedRouteError(component: WebLogComponent): ErrorResponse | undefined {
  const error = useRouteError();
  // Read ONCE: the rendering and the reporting must not disagree about whether
  // this is a routed outcome.
  const routeError = isRouteErrorResponse(error) ? error : undefined;

  useEffect(() => {
    if (routeError !== undefined) {
      // Every field the boundary was handed survives into the report — the whole
      // point of not routing an `ErrorResponse` through the `Error` fallback.
      const context = { component, status: routeError.status };
      const message = `Route error response: ${routeError.status} ${routeError.statusText}`;
      if (routeError.status >= OBSERVED_FAULT_STATUS) clientLogger.error(context, message);
      else clientLogger.warn(context, message);
      return;
    }
    // Not a routed verdict: a genuine crash. `String(...)` is the right answer
    // HERE and was only ever wrong for values that carry their own identity.
    clientLogger.error(
      { error: error instanceof Error ? error : new Error(String(error)), component },
      'Route error caught'
    );
  }, [component, error, routeError]);

  return routeError;
}
